import { BaseDomainModel } from '../models/base/base-domain-model';
import { inject, Injectable } from '@angular/core';
import { EmployersAPI } from '../api/employers-api';
import { Observable, throwError } from 'rxjs';
import { EmployerUser } from '../models/users/dto/employer-user';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ODataResponse } from '../models/protocols/odata-response';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ChangeEmailRequest } from '../models/users/requests/change-email-request';
import { HttpResponse } from '@angular/common/http';
import { EmployerPlanDesignatedUnit } from '../models/employers/plans/employer-plan-designated-unit';
import { EmployerPlanDesignatedUnitVersion } from '../models/employers/plans/employer-plan-designated-unit-version';
import { EmployerSearchResult } from '../models/employers/dto/employer-search-result';
import { DesignatedUnitSearchResult } from '../models/employers/plans/designated-unit-search-result';
import { Employer } from '../models/users/dto/employer';
import { PortalService } from '../services/portal/portal.service';
import { UpdateEmployerReq } from '../models/employers/dto/update-employer-req';
import { EmployerCRANumberInUseResponse } from '../models/employers/dto/employer-cra-number-in-use-response';
import { IpEmployerSubmission } from '../models/submissions/employer-submissions/ip-employer-submission';
import { UpdateEmployerAddressRequest } from '../models/users/requests/submissionRequests/update-employer-address-request';
import { IpUpdateEmployerContactsRequest } from '../models/users/requests/submissionRequests/ip-update-employer-contacts-request';
import { EmployerRecordCommentRequest } from '../models/employers/employer-record-comment-request';
import { EmployerRecordComment } from '../models/employers/employer-record-comment';
import { Mentionable } from '../models/base/mentionable';
import { InternalUserCommentReadReceiptRequest } from '../models/users/requests/internal-user-comment-read-receipt-request';
import { OdataUtils } from '../utils/odata-utils';

// Provided by Logged In Scope
@Injectable()
export class EmployersDomainModel extends BaseDomainModel {
  private employersAPI = inject(EmployersAPI);
  private portalService = inject(PortalService);

  public getEmployer(employerId: number, opts = new ODataQueryOptions()): Observable<Employer> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.employersAPI.ipGetEmployerById(employerId, opts);
        } else {
          return this.employersAPI.epGetEmployer(opts);
        }
      }),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getEmployerUsers(
    oDataQueryOptions: ODataQueryOptions,
    employerId: number
  ): Observable<ODataResponse<EmployerUser>> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.employersAPI.ipGetEmployerUsers(oDataQueryOptions, employerId);
        } else {
          return this.employersAPI.getEmployerUsers(oDataQueryOptions);
        }
      })
    );
  }

  public getEmployerUserById(employerId: number, employerUserId: number): Observable<EmployerUser> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.employersAPI.ipGetEmployerUser(employerId, employerUserId);
        } else {
          return this.employersAPI.epGetEmployerUser(employerUserId);
        }
      })
    );
  }

  public forceChangeEmail(req: ChangeEmailRequest, u: EmployerUser): Observable<EmployerUser> {
    return this.employersAPI.forceChangeEmail(u, req);
  }

  public forceChangePassword(u: EmployerUser): Observable<HttpResponse<any>> {
    return this.employersAPI.forceResetPassword(u);
  }

  public getEmployerPlanDesignatedUnits(
    planId: number,
    odataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<EmployerPlanDesignatedUnit>> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.employersAPI.ipGetEmployerPlanDesignatedUnits(planId, odataQueryOptions);
        } else {
          return this.employersAPI.epGetEmployerPlanDesignatedUnits(planId, odataQueryOptions);
        }
      })
    );
  }

  public ipCreateEmployerPlanDesignatedUnitVersion(
    req: EmployerPlanDesignatedUnitVersion
  ): Observable<EmployerPlanDesignatedUnitVersion> {
    return this.employersAPI.ipCreateEmployerPlanDesignatedUnitVersion(req);
  }

  public updateEmployerPlanDesignatedUnitVersion(
    req: EmployerPlanDesignatedUnitVersion
  ): Observable<EmployerPlanDesignatedUnitVersion> {
    return this.employersAPI.updateEmployerPlanDesignatedUnitVersion(req);
  }

  public ipUpdateEmployerPlanDesignatedUnit(req: EmployerPlanDesignatedUnit): Observable<EmployerPlanDesignatedUnit> {
    return this.employersAPI.ipUpdateEmployerPlanDesignatedUnit(req);
  }

  public searchEmployersByIdOrName(searchString: string): Observable<EmployerSearchResult[]> {
    const queryOptions = new ODataQueryOptions();
    const searchStringIsId = !isNaN(Number(searchString));
    queryOptions.setExpand('GoverningJurisdiction');
    if (searchStringIsId) {
      queryOptions.setFilter(`id eq ${searchString}`);
    } else {
      queryOptions.setFilter(`contains(Name, '${OdataUtils.escapeStringLiteral(searchString)}')`);
    }
    return this.employersAPI.searchEmployersByIdOrName(queryOptions).pipe(map(r => r.value));
  }

  public searchActiveDesignatedUnitsByIdOrName(
    searchString: string,
    employerPlanId: number
  ): Observable<DesignatedUnitSearchResult[]> {
    const queryOptions = new ODataQueryOptions();
    const searchStringIsId = /^[0-9]+$/.test(searchString);
    const escapedSearchString = OdataUtils.escapeStringLiteral(searchString);
    if (searchStringIsId) {
      queryOptions.setFilter(`clientUnitId eq '${escapedSearchString}'`);
    } else {
      queryOptions.setFilter(`contains(UnitName, '${escapedSearchString}')`);
    }
    return this.getActiveDesignatedUnitsForEmployer(employerPlanId, queryOptions);
  }

  public getActiveDesignatedUnitsForEmployer(
    employerPlanId: number,
    queryOptions: ODataQueryOptions = new ODataQueryOptions()
  ): Observable<DesignatedUnitSearchResult[]> {
    return this.portalService.isInternal$.pipe(
      take(1),
      switchMap(isInternal => {
        if (isInternal) {
          return this.employersAPI.searchInternalActiveDesignatedUnitsByIdOrName(queryOptions, employerPlanId);
        } else {
          return this.employersAPI.searchEmployerActiveDesignatedUnitsByIdOrName(queryOptions, employerPlanId);
        }
      }),
      map(r => r.value)
    );
  }

  public getEmployerCRANumberInUse(craNumber: string): Observable<EmployerCRANumberInUseResponse> {
    return this.employersAPI.getEmployerCRANumberInUse(craNumber);
  }

  public ipUpdateEmployer(req: UpdateEmployerReq): Observable<Employer> {
    return this.employersAPI.ipUpdateEmployer(req);
  }

  public ipUpdateEmployerAddress(req: UpdateEmployerAddressRequest): Observable<IpEmployerSubmission> {
    return this.employersAPI.ipUpdateEmployerAddress(req);
  }

  public ipUpdateEmployerContacts(request: IpUpdateEmployerContactsRequest): Observable<IpEmployerSubmission> {
    return this.employersAPI.ipUpdateEmployerContacts(request);
  }

  public ipGetEmployerCommentMentionables(opts: ODataQueryOptions): Observable<Mentionable[]> {
    return this.employersAPI.ipGetEmployerCommentMentionables(opts).pipe(map(r => r.value));
  }

  public ipAddEmployerRecordComment(req: EmployerRecordCommentRequest): Observable<EmployerRecordComment[]> {
    return this.employersAPI.ipAddEmployerRecordComment(req);
  }

  public ipGetCommentsForEmployer(employerId: string): Observable<EmployerRecordComment[]> {
    return this.employersAPI.ipGetCommentsForEmployer(employerId).pipe(map(r => r.value));
  }

  public ipSetEmployerCommentsAsRead(req: InternalUserCommentReadReceiptRequest): Observable<HttpResponse<any>> {
    return this.employersAPI.ipSetEmployerCommentsAsRead(req);
  }
}
